import { usePhotosStore } from "@/features/camera/hooks/usePhotoStore";
import { services as s } from "@/services";
import { Camera, ImagePlus, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import Webcam from "react-webcam";
import { toast } from "@patient-watch/ui/sonner";
import { getToastCopy } from "@/features/i18n/toastCopy";

import type { InputT } from "../../types/database/diaries";
import { ui } from "@patient-watch/ui";

interface Props {
    input: InputT;
}

function PhotoCamera({ onCapture, onClose }: { onCapture: (dataUrl: string) => void; onClose: () => void }) {
    const webcamRef = useRef<Webcam | null>(null);

    const handleCapture = useCallback(() => {
        const imageSrc = webcamRef.current?.getScreenshot();
        if (!imageSrc) return;
        onCapture(imageSrc);
        onClose();
    }, [onCapture, onClose]);

    return (
        <section className="top-0 left-0 z-50 fixed flex flex-col justify-between items-center bg-black w-[100vw] h-[100vh]">
            <div className="flex justify-between items-center px-4 py-3 w-full text-white">
                <p className="font-medium text-lg">Take a photo</p>
                <ui.Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-white hover:text-white"
                    onClick={onClose}
                >
                    Cancel
                </ui.Button>
            </div>
            <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "environment" }}
                className="w-full max-h-[70vh] object-cover"
            />
            <div className="px-4 py-6 w-full">
                <ui.Button type="button" className="w-full h-12" onClick={handleCapture}>
                    Capture photo
                </ui.Button>
            </div>
        </section>
    );
}

const Photo = (props: Props) => {
    const photos = usePhotosStore();
    const storedPhotos = usePhotosStore((state) => state.photos);
    const { setValue } = useFormContext();
    const inputId = props.input.input_id;
    const [cameraOpen, setCameraOpen] = useState(false);

    useEffect(() => {
        if (!inputId) return;
        setValue(
            inputId,
            storedPhotos.map((photo) => photo.img).filter((img) => img.length > 0),
            { shouldDirty: true }
        );
    }, [inputId, setValue, storedPhotos]);

    const addImage = (img: string) => {
        photos.addPhotos([
            {
                label: props.input.input_label ?? "",
                checked: false,
                delay: 0,
                img,
            },
        ]);
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(event.target.files || []);

        const validFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
        const invalidFiles = selectedFiles.filter((file) => !file.type.startsWith("image/"));

        if (invalidFiles.length > 0) {
            toast.error(getToastCopy().pleaseSelectFile);
        }

        const validImgs = await Promise.all(
            validFiles.map(async (file) => {
                return s.convertFileToBase64(file);
            })
        );

        photos.addPhotos(
            validImgs.map((img) => ({
                label: props.input.input_label ?? "",
                checked: false,
                delay: 0,
                img,
            }))
        );
    };

    const tileClass =
        "flex flex-col justify-center items-center gap-2 bg-white hover:bg-primary/5 dark:bg-slate-900 px-3 py-5 border-2 border-slate-300 hover:border-primary dark:border-slate-600 border-dashed rounded-xl w-full font-medium text-slate-800 dark:text-slate-100 text-lg text-center transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";

    return (
        <div className="flex flex-col gap-3">
            {cameraOpen ? (
                <PhotoCamera
                    onClose={() => setCameraOpen(false)}
                    onCapture={(dataUrl) => {
                        addImage(dataUrl);
                    }}
                />
            ) : null}
            <p className="text-slate-600 dark:text-slate-400 text-lg">
                Take a photo with your camera, or choose one from your device.
            </p>
            <div className="gap-3 grid grid-cols-2">
                <button type="button" className={tileClass} onClick={() => setCameraOpen(true)}>
                    <Camera className="size-7 text-primary" aria-hidden />
                    Take a photo
                </button>
                <label className={tileClass}>
                    <ImagePlus className="size-7 text-primary" aria-hidden />
                    Choose from device
                    <input
                        className="sr-only"
                        type="file"
                        accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                        multiple={props.input.allow_multiple}
                        onChange={(event) => {
                            void handleFileChange(event);
                            event.target.value = "";
                        }}
                    />
                </label>
            </div>
            {storedPhotos.length > 0 ? (
                <ul className="gap-2 grid grid-cols-2 sm:grid-cols-3" aria-label="Added photos">
                    {storedPhotos.map((photo) => (
                        <li
                            key={photo.id}
                            className="relative border border-slate-200 dark:border-slate-700 rounded-lg aspect-square overflow-hidden"
                        >
                            <img className="size-full object-cover" src={photo.img} alt={photo.label} />
                            <button
                                type="button"
                                onClick={() => photos.removePhoto(photo.id)}
                                className="top-1.5 right-1.5 absolute flex justify-center items-center bg-white/90 hover:bg-white shadow rounded-full size-9 text-slate-700"
                                aria-label="Remove photo"
                            >
                                <X className="size-5" aria-hidden />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
};

export default Photo;
